// Component: PerformanceMetrics.jsx
// Purpose: 6-box metric grid — live unrealized P&L, realized P&L, trade stats, and analytics

//---------------------------------------------------------------------------------------------------
//  ?                              PERFORMANCE METRICS
//  @author         :  DeeTrade Team
//  @createdOn      :  04/10/2026
//  @description    :  Responsive 6-metric grid. Unrealized P&L is live via useLivePnL; the five
//                     family figures (realized P&L, total trades, win rate, max drawdown, Sharpe)
//                     are rendered straight from the OMS /algo/summary family row passed as
//                     `performance` -- no browser recalculation (issue 10502-3).
//---------------------------------------------------------------------------------------------------

import React from 'react';
import { useLivePnL } from '../../contexts/TradingDataContext';

/*>>> HELPERS <<<*/

/**
 * Format a P&L number as an Indian locale string with + prefix for positive values.
 * @param {number} value
 * @returns {string}
 */
const formatPnl = (value) => {
  const formatted = Math.abs(value).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return value >= 0 ? `+${formatted}` : `-${formatted}`;
};

/*>>> SUB-COMPONENTS <<<*/

/**
 * Single metric display box — matches MonitoringTab MetricBox pattern.
 */
const MetricBox = ({ label, value, valueColor, first, note }) => (
  <div className={`px-4 py-3 ${first ? '' : 'lg:border-l lg:border-divider'}`}>
    <p className="text-[11px] text-fg-2 mb-1">{label}</p>
    <p className={`text-xl font-semibold tabular-nums ${valueColor}`}>{value}</p>
    {note ? <p className="mt-0.5 text-[10px] text-fg-3 tabular-nums">{note}</p> : null}
  </div>
);

/*>>> MAIN COMPONENT <<<*/

/**
 * PerformanceMetrics — 6-box metric grid for a single strategy instance.
 *
 * //! `performance` is the OMS family summary row (/algo/summary) -- the single authority for
 * //! the five family figures. A null `performance` (not yet hydrated, e.g. right after a
 * //! contract roll) renders `--`, never a browser-computed under-count.
 *
 * @param {{ performance: object|null, position: object|null, strategyInstanceId: string }} props
 */
const PerformanceMetrics = ({ performance, position, strategyInstanceId }) => {
  //* Live unrealized P&L — ticks update at 200ms throttle
  const { unrealizedPnl } = useLivePnL(strategyInstanceId);

  //--> Family figures exist only once the summary arrived and at least one trade closed
  const hasTrades = performance != null && Number(performance.total_trades) > 0;
  const isNum = (v) => v != null && !Number.isNaN(Number(v));

  /*>>> METRIC 1: Unrealized P&L <<<*/
  const unrealizedDisplay = position ? formatPnl(unrealizedPnl) : '--';
  const unrealizedColor = position
    ? unrealizedPnl >= 0
      ? 'text-profit'
      : 'text-loss'
    : 'text-fg-2';

  /*>>> METRIC 2: Realized P&L <<<*/
  //! Realized P&L renders whenever the summary arrived with a numeric total_pnl (rev2-F5) --
  //! independent of total_trades; the trade-count-dependent figures stay gated on hasTrades.
  const realizedPnlValue = performance != null && isNum(performance.total_pnl) ? Number(performance.total_pnl) : null;
  //! 144-1: total_pnl is NET (gross - charges). When the OMS could not resolve every fill's
  //!  charges (charges_complete false) the net is null: say "unresolved", never a bare "--".
  const chargesUnresolved = performance != null && performance.charges_complete === false;
  const realizedDisplay = chargesUnresolved
    ? 'unresolved'
    : realizedPnlValue != null ? formatPnl(realizedPnlValue) : '--';
  const realizedColor =
    realizedPnlValue != null && !chargesUnresolved
      ? (realizedPnlValue >= 0 ? 'text-profit' : 'text-loss')
      : 'text-fg-2';
  const realizedNote =
    performance != null && isNum(performance.gross_pnl)
      ? `Gross ${formatPnl(Number(performance.gross_pnl))} · Charges ${
          isNum(performance.total_charges)
            ? Number(performance.total_charges).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : 'unresolved'
        }`
      : null;

  /*>>> METRIC 3: Total Trades <<<*/
  const totalTradesDisplay = hasTrades ? String(performance.total_trades) : '--';

  /*>>> METRIC 4: Win Rate <<<*/
  const winRateValue = hasTrades && isNum(performance.win_rate) ? Number(performance.win_rate) : null;
  let winRateDisplay = '--';
  let winRateColor = 'text-fg-2';
  if (winRateValue != null) {
    winRateDisplay = `${(winRateValue * 100).toFixed(1)}%`;
    //! Win rate is neutral (fg-1) per approved mockup
    if (winRateValue >= 0.6) {
      winRateColor = 'text-profit';
    } else if (winRateValue >= 0.4) {
      winRateColor = 'text-fg-1';
    } else {
      winRateColor = 'text-loss';
    }
  }

  /*>>> METRIC 5: Max Drawdown <<<*/
  const hasDrawdown = hasTrades && isNum(performance.max_drawdown);
  const drawdownValue = hasDrawdown ? Number(performance.max_drawdown) : null;
  //! F-4: a zero drawdown renders "0.00" in neutral colour (no "-0.00"); loss style only when > 0.
  const drawdownDisplay = hasDrawdown
    ? `${drawdownValue > 0 ? '-' : ''}${drawdownValue.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : '--';
  const drawdownColor = hasDrawdown ? (drawdownValue > 0 ? 'text-loss' : 'text-fg-1') : 'text-fg-2';

  /*>>> METRIC 6: Sharpe Ratio <<<*/
  const hasSharpe = hasTrades && isNum(performance.sharpe);
  const sharpeDisplay = hasSharpe ? Number(performance.sharpe).toFixed(2) : '--';
  const sharpeColor = hasSharpe ? 'text-fg-1' : 'text-fg-2';

  return (
    <div className="grid grid-cols-2 gap-y-1 rounded-xl bg-bg-2 py-2 shadow-[var(--shadow-sm)] md:grid-cols-3 lg:grid-cols-6">
      {/* --> Row 1: P&L metrics */}
      <MetricBox label="Unrealized P&L" first value={unrealizedDisplay} valueColor={unrealizedColor} />
      <MetricBox label="Net P&L (after charges)" value={realizedDisplay} valueColor={realizedColor} note={realizedNote} />
      <MetricBox label="Total trades" value={totalTradesDisplay} valueColor="text-fg-1" />

      {/* --> Row 2: Performance analytics */}
      <MetricBox label="Win rate" value={winRateDisplay} valueColor={winRateColor} />
      <MetricBox label="Max drawdown" value={drawdownDisplay} valueColor={drawdownColor} />
      <MetricBox label="Sharpe ratio" value={sharpeDisplay} valueColor={sharpeColor} />
    </div>
  );
};

export default PerformanceMetrics;
